import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Upload, Plus, Trash2, Package } from 'lucide-react';
import { apiRequest } from '../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_OPTIONS = [
  'Forging', 'Machining', 'Welding', 'Silver Plating', 'Painting',
  'Anodizing', 'Electroplating', 'Powder Coating', 'Heat Treatment', 'Assembly'
];

const emptyPart = {
  part_no: '',
  part_name: '',
  part_description: '',
  rfq_qty: '',
  category: [],
  quoted_price: '',
  estimated_business: '',
  drawing_file: null,
  drawing_file_name: '',
  drawing_url: '',
};

export default function RFQRegistration() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isExisting = searchParams.get('existing') === 'true';
  const prefilledCustomerCode = searchParams.get('customerCode');
  
  const [customers, setCustomers] = useState([]);
  const [onboardingCustomers, setOnboardingCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerCode, setCustomerCode] = useState(prefilledCustomerCode || '');
  const [customRFQId, setCustomRFQId] = useState('');
  const [rfqDate, setRfqDate] = useState(new Date().toISOString().split('T')[0]);
  const [buyerName, setBuyerName] = useState('');
  const [quotationDate, setQuotationDate] = useState('');
  const [quotationFile, setQuotationFile] = useState(null);
  const [quotationFileName, setQuotationFileName] = useState('');
  const [quotationUrl, setQuotationUrl] = useState('');
  
  // Multi-part state
  const [parts, setParts] = useState([{ ...emptyPart }]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCustomers();
    fetchOnboardingCustomers();
  }, []);

  useEffect(() => {
    if (customerCode) {
      const customer = customers.find(c => c.customer_code === customerCode);
      setSelectedCustomer(customer);
      if (customer && customer.rfq_id_prefix) {
        setCustomRFQId(customer.rfq_id_prefix);
      }
    } else {
      setSelectedCustomer(null);
      setCustomRFQId('');
    }
  }, [customerCode, customers]);

  const fetchCustomers = async () => {
    try {
      const data = await apiRequest('/api/customers');
      setCustomers(data.customers || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  };

  const fetchOnboardingCustomers = async () => {
    try {
      const data = await apiRequest('/api/potential-customers');
      setOnboardingCustomers(data.potential_customers || []);
    } catch (error) {
      console.error('Failed to fetch onboarding customers:', error);
    }
  };

  const addPart = () => {
    setParts([...parts, { ...emptyPart }]);
  };

  const removePart = (index) => {
    if (parts.length > 1) {
      setParts(parts.filter((_, i) => i !== index));
    }
  };

  const updatePart = (index, field, value) => {
    const newParts = [...parts];
    newParts[index][field] = value;
    setParts(newParts);
  };

  const togglePartCategory = (index, cat) => {
    const newParts = [...parts];
    const currentCategories = newParts[index].category || [];
    if (currentCategories.includes(cat)) {
      newParts[index].category = currentCategories.filter(c => c !== cat);
    } else {
      newParts[index].category = [...currentCategories, cat];
    }
    setParts(newParts);
  };

  const handlePartFileUpload = async (index, file) => {
    console.log("UPLOAD TRIGGERED - Part", index, file);
    if (!file) {
      console.log("No file selected");
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log("Sending upload request to:", `${API_URL}/api/upload/drawing`);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/upload/drawing`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      console.log("Response status:", response.status);
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      console.log("Upload success:", data);
      const newParts = [...parts];
      newParts[index].drawing_file_name = file.name;
      newParts[index].drawing_url = data.url;
      setParts(newParts);
      toast.success(`Drawing uploaded for Part ${index + 1}`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error('Failed to upload drawing');
    }
  };

  const handleQuotationUpload = async (file) => {
    console.log("QUOTATION UPLOAD TRIGGERED", file);
    if (!file) {
      console.log("No quotation file selected");
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      console.log("Sending quotation upload to:", `${API_URL}/api/upload/quotation`);
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/upload/quotation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      console.log("Quotation response status:", response.status);
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      console.log("Quotation upload success:", data);
      setQuotationFileName(file.name);
      setQuotationUrl(data.url);
      toast.success('Quotation file uploaded');
    } catch (error) {
      console.error("Quotation upload error:", error);
      toast.error('Failed to upload quotation');
    }
  };

  const calculateTotalEstimate = () => {
    return parts.reduce((sum, part) => {
      const qty = parseFloat(part.rfq_qty) || 0;
      const price = parseFloat(part.quoted_price) || 0;
      return sum + (qty * price);
    }, 0);
  };

  const calculateTotalQty = () => {
    return parts.reduce((sum, part) => sum + (parseInt(part.rfq_qty) || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!customerCode) {
      toast.error('Please select a customer');
      return;
    }
    
    // Validate parts
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (!part.part_no || !part.part_name || !part.rfq_qty) {
        toast.error(`Part ${i + 1}: Part No, Part Name, and Quantity are required`);
        return;
      }
    }
    
    setSubmitting(true);
    
    try {
      const payload = {
        customer_code: customerCode,
        custom_rfq_id: customRFQId || null,
        rfq_date: rfqDate,
        buyer_name: buyerName || null,
        quotation_date: quotationDate || null,
        quotation_file_name: quotationFileName || null,
        quotation_url: quotationUrl || null,
        parts: parts.map(p => ({
          part_no: p.part_no,
          part_name: p.part_name,
          part_description: p.part_description || null,
          rfq_qty: parseInt(p.rfq_qty) || 0,
          category: p.category || [],
          drawing_file_name: p.drawing_file_name || null,
          drawing_url: p.drawing_url || null,
          quoted_price: parseFloat(p.quoted_price) || null,
        }))
      };
      
      const response = await apiRequest('/api/rfq', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      toast.success(`RFQ ${response.rfq_id} created successfully with ${parts.length} part(s)`);
      navigate('/reports');
    } catch (error) {
      toast.error(error.message || 'Failed to create RFQ');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div data-testid="rfq-registration-page">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-[#0A2540]">
            {isExisting ? 'Register RFQ for Existing Customer' : 'RFQ Registration'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">Create a new Request for Quotation with multiple parts</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Customer & RFQ Info */}
          <Card className="p-6 mb-6">
            <h3 className="text-lg font-semibold text-[#0A2540] mb-4">Customer & RFQ Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>Customer Code *</Label>
                <Select value={customerCode} onValueChange={setCustomerCode}>
                  <SelectTrigger data-testid="select-customer">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.length > 0 && (
                      <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-100">Existing Customers</div>
                    )}
                    {customers.map((c) => (
                      <SelectItem key={c.customer_code} value={c.customer_code}>
                        {c.customer_code} - {c.company_name}
                      </SelectItem>
                    ))}
                    {onboardingCustomers.length > 0 && (
                      <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-orange-50 mt-1">Onboarding Customers</div>
                    )}
                    {onboardingCustomers.map((c) => (
                      <SelectItem key={`onb-${c._id}`} value={c.company_name}>
                        {c.company_name} (Onboarding)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>RFQ ID (Optional)</Label>
                <Input
                  data-testid="input-rfq-id"
                  value={customRFQId}
                  onChange={(e) => setCustomRFQId(e.target.value)}
                  placeholder="Auto-generated if empty"
                />
              </div>
              
              <div>
                <Label>RFQ Date *</Label>
                <Input
                  type="date"
                  data-testid="input-rfq-date"
                  value={rfqDate}
                  onChange={(e) => setRfqDate(e.target.value)}
                  required
                />
              </div>
              
              <div>
                <Label>Buyer Name</Label>
                <Input
                  data-testid="input-buyer-name"
                  value={buyerName}
                  onChange={(e) => setBuyerName(e.target.value)}
                  placeholder="Enter buyer name"
                />
              </div>
            </div>
            
            {selectedCustomer && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Company:</strong> {selectedCustomer.company_name} | 
                  <strong> Location:</strong> {selectedCustomer.city}, {selectedCustomer.state}
                </p>
              </div>
            )}
          </Card>

          {/* Parts Section */}
          <Card className="p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-[#0A2540]" />
                <h3 className="text-lg font-semibold text-[#0A2540]">Parts ({parts.length})</h3>
              </div>
              <Button
                type="button"
                onClick={addPart}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="add-part-btn"
              >
                <Plus className="w-4 h-4" />
                Add Part
              </Button>
            </div>

            <div className="space-y-6">
              {parts.map((part, index) => (
                <div 
                  key={index} 
                  data-testid={`part-${index}`}
                  className="p-4 border rounded-lg bg-gray-50"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-[#0A2540]">Part {index + 1}</h4>
                    {parts.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePart(index)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`remove-part-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label>Part No *</Label>
                      <Input
                        data-testid={`part-${index}-part-no`}
                        value={part.part_no}
                        onChange={(e) => updatePart(index, 'part_no', e.target.value)}
                        placeholder="Enter part number"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label>Part Name *</Label>
                      <Input
                        data-testid={`part-${index}-part-name`}
                        value={part.part_name}
                        onChange={(e) => updatePart(index, 'part_name', e.target.value)}
                        placeholder="Enter part name"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label>Quantity *</Label>
                      <Input
                        type="number"
                        data-testid={`part-${index}-qty`}
                        value={part.rfq_qty}
                        onChange={(e) => updatePart(index, 'rfq_qty', e.target.value)}
                        placeholder="Enter quantity"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label>Quoted Price (₹)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        data-testid={`part-${index}-price`}
                        value={part.quoted_price}
                        onChange={(e) => updatePart(index, 'quoted_price', e.target.value)}
                        placeholder="Price per unit"
                      />
                    </div>
                    
                    <div>
                      <Label>Est. Business (₹)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        data-testid={`part-${index}-est-business`}
                        value={part.estimated_business}
                        onChange={(e) => updatePart(index, 'estimated_business', e.target.value)}
                        placeholder="Projected business value"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label>Part Description</Label>
                    <Textarea
                      data-testid={`part-${index}-desc`}
                      value={part.part_description}
                      onChange={(e) => updatePart(index, 'part_description', e.target.value)}
                      placeholder="Enter part description"
                      rows={2}
                    />
                  </div>

                  <div className="mt-4">
                    <Label>Category</Label>
                    <div className="flex flex-wrap gap-3 mt-2">
                      {CATEGORY_OPTIONS.map((cat) => (
                        <label key={cat} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={part.category?.includes(cat)}
                            onCheckedChange={() => togglePartCategory(index, cat)}
                          />
                          <span className="text-sm">{cat}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label>Drawing File</Label>
                    <div className="flex items-center gap-4 mt-2">
                      <label className="flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-gray-100">
                        <Upload className="w-4 h-4" />
                        <span className="text-sm">Upload Drawing</span>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => handlePartFileUpload(index, e.target.files[0])}
                        />
                      </label>
                      {part.drawing_file_name && (
                        <span className="text-sm text-green-600">{part.drawing_file_name}</span>
                      )}
                    </div>
                  </div>

                  {part.quoted_price && part.rfq_qty && (
                    <div className="mt-4 p-2 bg-green-50 rounded text-sm">
                      <strong>Part Estimate:</strong> ₹{(parseFloat(part.quoted_price) * parseInt(part.rfq_qty)).toLocaleString('en-IN')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Quotation Section */}
          <Card className="p-6 mb-6">
            <h3 className="text-lg font-semibold text-[#0A2540] mb-4">Quotation (Optional)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Quotation Date</Label>
                <Input
                  type="date"
                  data-testid="input-quotation-date"
                  value={quotationDate}
                  onChange={(e) => setQuotationDate(e.target.value)}
                />
              </div>
              
              <div>
                <Label>Quotation File</Label>
                <div className="flex items-center gap-4 mt-2">
                  <label className="flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer hover:bg-gray-100">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">Upload Quotation</span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.xlsx,.xls,.doc,.docx"
                      onChange={(e) => handleQuotationUpload(e.target.files[0])}
                    />
                  </label>
                  {quotationFileName && (
                    <span className="text-sm text-green-600">{quotationFileName}</span>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* Summary */}
          <Card className="p-6 mb-6 bg-[#0A2540] text-white">
            <h3 className="text-lg font-semibold mb-4">RFQ Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-300">Total Parts</p>
                <p className="text-2xl font-bold">{parts.length}</p>
              </div>
              <div>
                <p className="text-sm text-gray-300">Total Quantity</p>
                <p className="text-2xl font-bold">{calculateTotalQty().toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-300">Estimated Business</p>
                <p className="text-2xl font-bold">₹{calculateTotalEstimate().toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-300">Customer</p>
                <p className="text-lg font-semibold truncate">{selectedCustomer?.company_name || '-'}</p>
              </div>
            </div>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#0A2540] hover:bg-[#0d2f52]"
              data-testid="submit-rfq"
            >
              {submitting ? 'Creating RFQ...' : `Create RFQ with ${parts.length} Part(s)`}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
